import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Lock, Mail, Eye, EyeOff, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { errorDetail } from "@/lib/api";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const next = params.get("next") || "/";
  if (user) return <Navigate to={next.startsWith("/") ? next : "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return setError("E-posta ve şifre gerekli");
    setBusy(true);
    setError("");
    try {
      await login(email.trim(), password);
      navigate(next.startsWith("/") ? next : "/", { replace: true });
    } catch (err) {
      setError(errorDetail(err, "Giriş yapılamadı. Lütfen tekrar deneyin."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] flex-col bg-secondary/30 grain">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center px-6 py-16 lg:flex-row lg:items-center lg:gap-24">
        <div className="mb-12 max-w-md fade-up lg:mb-0 lg:flex-1">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-foreground text-background">
              <Sparkles className="h-5 w-5" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <div className="text-base font-extrabold tracking-tight">Verba</div>
              <div className="text-[11px] text-muted-foreground">Bilgi Asistanı Platformu</div>
            </div>
          </div>
          <h1 className="mt-10 text-4xl font-extrabold tracking-tight sm:text-5xl">Yönetim paneli</h1>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Dokümanlarınızı yükleyin, asistanınızı eğitin ve tek linkle yayınlayın.
            Bu alan yalnızca yöneticiler içindir.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            Paylaşılan asistan linkleri girişsiz çalışmaya devam eder
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="login-form"
          className="soft-card w-full max-w-md p-8 fade-up lg:flex-none"
          style={{ animationDelay: "80ms" }}
        >
          <div className="mb-6">
            <div className="text-lg font-bold tracking-tight">Giriş yap</div>
            <div className="mt-1 text-sm text-muted-foreground">Yönetici hesabınızla devam edin.</div>
          </div>

          <label className="block text-xs font-semibold text-muted-foreground" htmlFor="login-email">E-posta</label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-input bg-background px-3 transition-shadow focus-within:ring-2 focus-within:ring-ring/40">
            <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@sirket.com"
              className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
            />
          </div>

          <label className="mt-4 block text-xs font-semibold text-muted-foreground" htmlFor="login-password">Şifre</label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-input bg-background px-3 transition-shadow focus-within:ring-2 focus-within:ring-ring/40">
            <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              id="login-password"
              data-testid="login-password-input"
              type={show ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
            />
            <button
              type="button"
              data-testid="login-toggle-password-btn"
              onClick={() => setShow((s) => !s)}
              className="text-muted-foreground transition-colors hover:text-foreground"
              aria-label={show ? "Şifreyi gizle" : "Şifreyi göster"}
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {error && (
            <div data-testid="login-error" className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={busy}
            className="mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-foreground text-sm font-semibold text-background transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Giriş yapılıyor..." : "Giriş yap"}
            {!busy && <ArrowRight className="h-4 w-4" />}
          </button>

          <p className="mt-5 text-center text-[11px] text-muted-foreground">
            Oturum 8 saat sonra otomatik olarak sona erer.
          </p>
        </form>
      </div>
    </div>
  );
}
